import React from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { Box, Button, Typography, Paper } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";

import { WorkflowStepData, StepStatus } from "../../../types/process";

export interface CustomStepNodeData {
  step: WorkflowStepData;
  onNodeClick: (step: WorkflowStepData) => void;
}

const CustomStepNode: React.FC<NodeProps<CustomStepNodeData>> = ({ data }) => {
  const { t } = useTranslation();
  const { step, onNodeClick } = data;

  const getStatusIcon = () => {
    if (step.status === StepStatus.COMPLETE) {
      return <CheckCircleIcon sx={{ fontSize: 20, color: "#10b981" }} />;
    }
    if (step.requiresConfiguration) {
      return <HelpOutlineIcon sx={{ fontSize: 20, color: "#f59e0b" }} />;
    }
    return <ErrorOutlineIcon sx={{ fontSize: 20, color: "#ef4444" }} />;
  };

  let borderColor = "#e5e7eb";
  let backgroundColor = "#ffffff";
  if (step.status === StepStatus.COMPLETE) {
    borderColor = "#10b981";
    backgroundColor = "#f0fdf4";
  } else if (step.requiresConfiguration) {
    borderColor = "#f59e0b";
    backgroundColor = "#fffbeb";
  } else {
    borderColor = "#ef4444";
    backgroundColor = "#fef2f2";
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor,
        border: `2px solid ${borderColor}`,
        width: 256,
        minHeight: 110,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "#9333ea",
          height: 16,
          width: 8,
          borderRadius: 4,
          border: "1px solid #7c3aed",
        }}
      />
      <Box>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
          <Box sx={{ mr: 1, mt: 0.5, flexShrink: 0 }}>{getStatusIcon()}</Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "bold",
              color: "#36276F",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
            title={step.name}
          >
            {step.name}
          </Typography>
        </Box>
        {step.status === StepStatus.COMPLETE &&
          step.currentConfig?.selectedAppId &&
          step.config?.availableApps && (
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                display: "block",
                mb: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {t("using")}:{" "}
              {step.config.availableApps.find(
                app => app.id === step.currentConfig?.selectedAppId,
              )?.name || "N/A"}
            </Typography>
          )}
      </Box>
      <Button
        variant="outlined"
        size="small"
        fullWidth
        onClick={() => onNodeClick(step)}
        sx={{
          mt: "auto",
          textTransform: "none",
          fontSize: "0.75rem",
          py: 0.5,
          backgroundColor: "white",
          "&:hover": {
            backgroundColor: "#f3f4f6",
            boxShadow: 1,
          },
        }}
      >
        {step.status === StepStatus.INCOMPLETE
          ? t("configure")
          : t("view_details")}
      </Button>
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "#9333ea",
          height: 16,
          width: 8,
          borderRadius: 4,
          border: "1px solid #7c3aed",
        }}
      />
    </Paper>
  );
};

export default CustomStepNode;
